import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { Button, Icon, Message } from 'semantic-ui-react';

import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';

import { MAX_ALLOWED_IMAGES } from './FileSelection.constants';
import { ReactReferencePropTypes } from '../../../../../libs/proptypes';
import { SOURCE_TYPE_DEFAULT } from '../../constants/sources';
import { BusinessFilePropType } from '../../types/files';
import FilesManagementModalProvider from '../FilesManagementModalProvider';
import ImagesDeck from '../ImagesDeck';

import './FileSelection.scss';

const propTypes = {
  initialFiles: PropTypes.arrayOf(BusinessFilePropType),
  files: PropTypes.arrayOf(BusinessFilePropType),
  onUpdateFiles: PropTypes.func.isRequired,
  uploadSourceType: PropTypes.string,
  maxAllowedImages: PropTypes.number,
  hideImagesDeck: PropTypes.bool,
  onImagesDeckFileEdit: PropTypes.func,
  hideEditButton: PropTypes.bool,
  editButtonText: PropTypes.string,
  editButtonRef: ReactReferencePropTypes,
  noImagesSelectedText: PropTypes.string,
  renderEditButton: PropTypes.func,
  renderSelectedImages: PropTypes.func,
};

const defaultProps = {
  initialFiles: [],
  files: null,
  uploadSourceType: SOURCE_TYPE_DEFAULT,
  maxAllowedImages: MAX_ALLOWED_IMAGES,
  hideImagesDeck: false,
  onImagesDeckFileEdit: null,
  hideEditButton: false,
  editButtonText: 'Edit images',
  editButtonRef: null,
  noImagesSelectedText: '',
  renderEditButton: null,
  renderSelectedImages: null,
};

function FileSelection({
  initialFiles,
  files,
  onUpdateFiles,
  uploadSourceType,
  maxAllowedImages,
  hideImagesDeck,
  onImagesDeckFileEdit,
  hideEditButton,
  editButtonText,
  editButtonRef,
  noImagesSelectedText,
  renderEditButton,
  renderSelectedImages,
}) {
  const isControlled = !_isNil(files);

  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [localFiles, setLocalFiles] = useState(isControlled ? files : initialFiles);

  async function onUpdateLocalFiles(newFiles) {
    setLocalFiles(newFiles);
    await onUpdateFiles(newFiles);
  }

  function onEditButtonClick() {
    setFileModalOpen(true);
  }

  useEffect(() => {
    if (isControlled) {
      setLocalFiles(files);
    }
  }, [files]);

  const imagesSelected = !_isEmpty(localFiles);

  return (
    <div className="file-selection">
      {!hideEditButton && _isNil(renderEditButton) && (
        <div className="buttons default">
          <Button icon={_isEmpty(editButtonText)} onClick={onEditButtonClick} ref={editButtonRef}>
            {editButtonText}
            <Icon name="pencil" />
          </Button>
        </div>
      )}

      {!_isNil(renderEditButton) && renderEditButton({ onClick: onEditButtonClick })}

      {!hideImagesDeck && _isNil(renderSelectedImages) && (
        <ImagesDeck
          files={localFiles}
          onUpdateFilesOrdering={onUpdateLocalFiles}
          onFileEdit={onImagesDeckFileEdit}
        />
      )}

      {imagesSelected && !_isNil(renderSelectedImages) && (
        <div className="selected-images-container">
          {renderSelectedImages({ selectedFiles: localFiles, onChange: onEditButtonClick })}
        </div>
      )}

      {!imagesSelected && !_isEmpty(noImagesSelectedText) && (
        <Message warning content={noImagesSelectedText} />
      )}

      {fileModalOpen && (
        <FilesManagementModalProvider
          open={fileModalOpen}
          setOpen={setFileModalOpen}
          initialSelectedFiles={localFiles}
          minFilesAllowed={null}
          maxFilesAllowed={maxAllowedImages}
          onSubmit={onUpdateLocalFiles}
          uploadSourceType={uploadSourceType}
        />
      )}
    </div>
  );
}

FileSelection.propTypes = propTypes;
FileSelection.defaultProps = defaultProps;
export default FileSelection;
